/**
 * The identity of Claudia's full-screen conversation surface — the pair of ids that decides
 * which conversations the `/ai-assistant` page lists and creates.
 *
 * Shared rather than restated because both ends now write it. The page has always created its
 * threads with this profile/surface pair; since LAT-904 the backend creates one too
 * (`claudia.startDocumentsConversation`, the "Ask Claudia" handoff from the Documents screen),
 * and a thread stamped with anything else is invisible in the history list the user lands on.
 * Two literals in two packages is exactly the drift that would produce it.
 *
 * `CLAUDIA_FULL_SCREEN_PROFILE_ID` must stay equal to the backend's
 * `CLAUDIA_EXISTING_PROJECT_READWRITE_PROFILE_ID` (`assistant/profiles/registry.ts`), which is
 * the agent profile registry's own name for the same thing; a contract test pins the equality.
 */
export const CLAUDIA_FULL_SCREEN_PROFILE_ID = "existing-project-readwrite";
/** The runtime surface id every full-screen Claudia thread carries. */
export const CLAUDIA_FULL_SCREEN_SURFACE_ID = "chat-ui";
/**
 * How many conversations one project's history list returns, and therefore the most rows any
 * conversation surface can hold.
 *
 * Shared because both ends read it: the backend caps `claudia.listProjectConversations` here,
 * and the all-conversations screen filters client-side over exactly this window and its footer
 * has to say so. It lives beside the surface ids rather than in the backend's `inputLimits.ts`
 * because `threads/access.ts` sits inside the per-tool-call hot-path bundle, where one more
 * imported file is a measured cost (LAT-916) — and `@repo/shared` is already in that graph.
 */
export const MAX_THREAD_HISTORY = 120;
/**
 * How many conversations one bulk pin/archive call may touch. Each id costs a component thread
 * read plus a projected write, so the bound keeps a single transaction small; a larger
 * selection is sent as sequential batches of this size.
 */
export const MAX_BULK_THREAD_IDS = 50;
